// src/components/PaymentResult/index.js
//
// Unified Cashfree return-URL handler.
//
// Cashfree's old SDK (LoveyCom) sends ALL outcomes to a single returnUrl with
// txStatus appended: txStatus=SUCCESS | FAILED | CANCELLED | PENDING
//
// Cashfree's newer SDK sends: order_status=PAID | ACTIVE | EXPIRED | TERMINATED
// and transaction_status=SUCCESS | FAILED | USER_DROPPED | CANCELLED
//
// This component reads whichever params are present, calls the backend cancel API
// for non-success outcomes, and routes to /payment/success or /payment/cancel.

import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const PaymentResult = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const orderId  = searchParams.get('order');   // integer DB id
    const gateway  = searchParams.get('gateway') || 'cashfree';

    // ── Detect outcome from URL params ──────────────────────────────────
    const explicitFailure = detectExplicitFailure(searchParams);

    if (explicitFailure.failed) {
      console.log(`🛑 [PaymentResult] Explicit failure detected: gateway=${gateway} order=${orderId} reason=${explicitFailure.reason}`);
      
      // Tell the backend to cancel the order (fire-and-forget, don't block UI)
      if (orderId) {
        const token    = getValidAuthSession()?.token;
        const tenantId = getTenantId();
        axios.get(`${API_BASE_URL}/payment/cancel/${gateway}/${orderId}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'X-Api-Key': X_API_KEY,
            'X-Tenant': tenantId,
          }
        }).catch(err => {
          console.warn('[PaymentResult] Backend cancel call failed (non-blocking):', err?.message);
        });
      }

      window.location.replace(
        `/payment/cancel?gateway=${gateway}&order=${orderId}&reason=${encodeURIComponent(explicitFailure.reason)}`
      );
      return;
    }

    // Default: Hand off to PaymentSuccess which will poll backend / transactions to confirm payment status
    console.log(`✅ [PaymentResult] Forwarding to PaymentSuccess for order=${orderId} gateway=${gateway}`);
    window.location.replace(`/payment/success?${searchParams.toString()}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show a brief loading state while we decide where to go
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTop: '3px solid #f97316', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>Processing payment result…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

function detectExplicitFailure(params) {
  const txStatus    = params.get('txStatus')?.toUpperCase();
  const txnStatus   = params.get('transaction_status')?.toUpperCase();
  const orderStatus = params.get('order_status')?.toUpperCase();
  const txMsg       = params.get('txMsg');

  if (txStatus === 'CANCELLED' || txnStatus === 'USER_DROPPED' || txnStatus === 'CANCELLED') {
    return { failed: true, reason: 'dismissed' };
  }
  if (txStatus === 'FAILED' || txnStatus === 'FAILED') {
    return { failed: true, reason: txMsg || 'payment_failed' };
  }
  if (orderStatus === 'EXPIRED') {
    return { failed: true, reason: 'session_expired' };
  }
  if (orderStatus === 'TERMINATED') {
    return { failed: true, reason: 'payment_terminated' };
  }

  return { failed: false, reason: '' };
}

function getCancelReason(params) {
  const txStatus   = params.get('txStatus')?.toUpperCase();
  const txnStatus  = params.get('transaction_status')?.toUpperCase();
  const orderStatus = params.get('order_status')?.toUpperCase();
  const txMsg      = params.get('txMsg');

  if (txStatus === 'CANCELLED' || txnStatus === 'USER_DROPPED' || txnStatus === 'CANCELLED') {
    return 'dismissed';
  }
  if (txStatus === 'FAILED' || txnStatus === 'FAILED') {
    return txMsg || 'payment_failed';
  }
  if (orderStatus === 'EXPIRED') return 'session_expired';
  if (orderStatus === 'TERMINATED') return 'payment_terminated';

  return txMsg || 'payment_not_completed';
}

export default PaymentResult;
